/**
 * FASE 2 — Avisos e transparência (textos de confirmação).
 *
 * Funções PURAS: recebem o ESTADO já apurado pela tela e devolvem o texto que o
 * operador vê. Ficam num módulo próprio para (a) o MESMO aviso ser usado em
 * todas as telas, (b) o texto ser testável sem jsdom e (c) nenhum cálculo ser
 * escondido dentro de JSX.
 *
 * Nenhuma função aqui grava, chama API ou decide nada: só escrevem POR EXTENSO
 * o que a ação vai fazer.
 */

/**
 * FASE 1.3 + FASE 2 — Regerar chaves apaga lutas e colocações.
 *
 * Quando o campeonato já tem resultado lançado ou medalha registrada, o aviso
 * diz exatamente o que se perde — inclusive que o Ranking Federativo pode
 * perder linhas até ser reprocessado.
 */
export function buildRegenerateWarning({ colocacoes = 0, medalhas = 0 } = {}) {
  const base =
    'Regerar todas as chaves?\n\n' +
    'Isso irá APAGAR o progresso atual de todas as lutas (placar, vencedores, BYE) e gerar as chaves novamente com a lista atualizada de atletas confirmados.\n\n';
  let extra = '';
  if (colocacoes > 0 || medalhas > 0) {
    const partes = [];
    if (colocacoes > 0) partes.push(`${colocacoes} colocação(ões) no ranking ao vivo`);
    if (medalhas > 0) partes.push(`${medalhas} medalha(s) registrada(s)`);
    extra =
      'ATENÇÃO: este campeonato JÁ TEM ' + partes.join(' e ') + '.\n\n' +
      'As lutas e as colocações serão perdidas e o Ranking Federativo pode perder linhas até ser reprocessado.\n\n';
  }
  return base + extra + 'Deseja continuar?';
}

/**
 * FASE 2.2 / 2.3 / 5.3 — Lançamento Direto: o que a gravação substitui.
 *
 * A chave tem UMA fonte de medalha (Fase 1): o lançamento direto SUBSTITUI a
 * colocação e as medalhas daquela chave — as lutas não são alteradas. O aviso
 * lista o pódio que será gravado e o que já existe hoje na chave.
 */
export function buildDirectPlacementWarning({
  podio = '',
  jaTemLancamentoDireto = false,
  campeaoName = '',
  medalhasCount = 0,
  semColocacao = [],
  checagemFalhou = false,
} = {}) {
  let msg = 'Salvar o Lançamento Direto desta chave?\n\n';
  if (podio) msg += `Colocação que será gravada:\n${podio}\n\n`;

  if (jaTemLancamentoDireto) {
    msg +=
      'Esta chave JÁ TEM um Lançamento Direto registrado: a colocação atual será ' +
      'SUBSTITUÍDA por esta.\n\n';
  } else if (campeaoName) {
    msg +=
      `Esta chave JÁ TEM resultado lançado pelo Placar/Mesa Central (campeão: ${campeaoName}).\n` +
      'A colocação desta chave será SUBSTITUÍDA pela informada acima. As LUTAS não são alteradas.\n\n';
  }

  if (medalhasCount > 0) {
    msg +=
      `Esta chave JÁ TEM ${medalhasCount} medalha(s) registrada(s).\n` +
      'A chave fica com UMA fonte de medalha: as medalhas anteriores desta chave serão ' +
      'SUBSTITUÍDAS pelas do Lançamento Direto — nada é duplicado e nada é somado.\n\n';
  }

  // FASE 5 — quem NÃO entra no pódio não some: continua no ranking da chave,
  // sem colocação e com 0 ponto. O aviso lista esses atletas.
  if (semColocacao.length > 0) {
    msg +=
      'Os demais atletas desta chave NÃO desaparecem do ranking: continuam ' +
      'listados SEM COLOCAÇÃO e com 0 ponto:\n' +
      semColocacao.map((nome) => `  • ${nome}`).join('\n') +
      '\n\n';
  }

  if (checagemFalhou) {
    msg +=
      'Não foi possível conferir agora se esta chave já tem resultado ou medalha — ' +
      'confira antes de gravar.\n\n';
  }

  return msg + 'Confirmar?';
}

/**
 * FASE 5.2 — Desfazer o Lançamento Direto: o que será apagado e o que volta.
 *
 * A chave volta ao resultado das LUTAS (Placar/Mesa). Medalhas e entregas do
 * lançamento saem; a próxima premiação oficial volta a premiar a chave.
 */
export function buildUndoDirectPlacementWarning({
  podio = '',
  medalhasCount = 0,
  temResultadoDeLuta = false,
} = {}) {
  let msg = 'Desfazer o Lançamento Direto desta chave?\n\n';
  if (podio) msg += `A colocação lançada à mão será apagada:\n${podio}\n\n`;
  if (medalhasCount > 0) {
    msg += `Também serão apagadas ${medalhasCount} medalha(s) e as entregas geradas por este lançamento.\n\n`;
  }
  msg += temResultadoDeLuta
    ? 'A colocação volta a ser a do RESULTADO DAS LUTAS (Placar/Mesa Central).\n\n'
    : 'Esta chave não tem lutas decididas: ela ficará SEM colocação até os resultados serem lançados.\n\n';
  msg +=
    'A chave deixa de ter Lançamento Direto — a próxima premiação oficial volta a ' +
    'premiá-la a partir das lutas.\n\n';
  return msg + 'Confirmar?';
}

/**
 * FASE 2.1 — Premiar: o que a premiação faz e o que ela NÃO faz.
 *
 * O rótulo "Finalizado" do cadastro não premia: medalha só nasce na premiação.
 * Chaves decididas por Lançamento Direto são puladas (uma fonte por chave).
 */
export function buildAwardWarning({ medalhasCount = 0 } = {}) {
  let msg =
    'Premiar o campeonato agora?\n\n' +
    'A premiação grava as MEDALHAS a partir dos resultados das lutas (chaves com a final ' +
    'decidida) e marca o campeonato como Finalizado.\n\n' +
    'Chaves decididas por Lançamento Direto são PULADAS — cada chave tem uma fonte de ' +
    'medalha só (nunca dois ouros na mesma chave).\n\n' +
    'Premiar de novo não duplica medalhas. As medalhas são o que o Ranking Federativo e a ' +
    'página pública leem.\n\n';
  if (medalhasCount > 0) {
    msg += `Este campeonato já tem ${medalhasCount} medalha(s) registrada(s).\n\n`;
  }
  msg += 'Se você alterou dados em outra tela, salve antes. Confirmar?';
  return msg;
}
